import clsx from "clsx";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes, { InferProps } from "prop-types";
import React, { ReactElement } from "react";
import Img from "gatsby-image"

import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Collapse,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { Link } from "gatsby";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      position: "relative",
      marginBottom: "5px"
    },
    avatar: {
      cursor: "pointer",
      border: "1px solid black",
      color: "black"
    },
    cardContent: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      justifyContent: "space-between",
    },
    chips: {
      display: "grid",
      gap: `${theme.spacing(1)}px`,
      justifyContent: "stretch",
      justifyItems: "center",
      gridTemplateColumns: `repeat(auto-fill, minmax(100px, 1fr))`,
    },
    media: {
      cursor: "pointer",
      height: 0,
      paddingTop: "56.25%", // 16:9
    },
    expand: {
      transform: "rotate(0deg)",
      marginLeft: "auto",
      transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
      }),
    },
    outline: {
      border: "1px solid black",
      color: "black !important"
    },
    buyBadge: {
      position: "absolute",
      width: 130,
      height: 80,
      background: "#DC004E",
      color: "white",
      right: -48,
      transform: "rotate(40deg)",
      paddingTop: 60,
      paddingLeft: 5,
      marginTop: -32,
      lineHeight: "18px",
      textAlign: "center",
    },
    rentBadge: {
      position: "absolute",
      width: 120,
      height: 80,
      background: "#DC004E",
      color: "white",
      right: -48,
      transform: "rotate(40deg)",
      paddingTop: 40,
      paddingLeft: 5,
      marginTop: -32,
      lineHeight: "18px",
      textAlign: "center",
    }
  })
);

export const Property = ({
  property,
  filterOptions
}: InferProps<typeof Property.propTypes> = {}): ReactElement => {
  const classes = useStyles();
  const convertedPrice =
    property.frontmatter.rentalperiod < 2
      ? new Intl.NumberFormat("en-UK", {
        style: "currency",
        useGrouping: true,
        maximumSignificantDigits: 21,
        currency: "GBP",
      }).format(property.frontmatter.price)
      : new Intl.NumberFormat("en-UK", {
        style: "currency",
        useGrouping: true,
        maximumSignificantDigits: 21,
        currency: "GBP",
      }).format(
        property.frontmatter.rentalperiod === 4
          ? Math.round(property.frontmatter.price)
          : Math.round(property.frontmatter.price * 52 / 12)
      ) +
      "pcm" +
      " / " +
      new Intl.NumberFormat("en-UK", {
        style: "currency",
        useGrouping: true,
        maximumSignificantDigits: 21,
        currency: "GBP",
      }).format(
        property.frontmatter.rentalperiod === 4
          ? Math.round(property.frontmatter.price * 12 / 52)
          : Math.round(property.frontmatter.price)
      ) +
      "pw"

  const cardSubHeader =
    property.frontmatter.poa ? "P.O.A" :
      (property.frontmatter.pricetext ? `${property.frontmatter.pricetext} ${convertedPrice}` : convertedPrice);

  return (
    <Card className={classes.root} data-id={property.frontmatter.id}>
      {property.frontmatter.UO_LA && (
        <div className={classes.rentBadge}>
          <span>Let</span><br />
          <span>Agreed</span>
        </div>
      )}
      {property.frontmatter.sold === 2 && <div className={classes.buyBadge}>
        <span>Sold/STC</span>
      </div>}
      <CardHeader
        avatar={
          <Avatar
            aria-label="information"
            className={classes.avatar}
            onClick={(): void =>
              navigate(`/properties/${property.frontmatter.id}`, {
                state: { filterOptions }
              })
            }
          >
            <i className="material-icons">info_outline</i>
          </Avatar>
        }
        title={property.frontmatter.addressSa1}
        subheader={cardSubHeader}
      />
      {property.frontmatter.featuredimage && (
        <Link
          to={`/properties/${property.frontmatter.id}`}
          state={{ filterOptions }}
        >
          <Img
            className={classes.media}
            fluid={property.frontmatter.featuredimage.childImageSharp.fluid}
          />
        </Link>
      )}
      <CardContent className={clsx(classes.cardContent)}>
        <Typography paragraph>{property.excerpt}</Typography>
        <div className={clsx(classes.chips)}>
          {property.frontmatter.bedrooms > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.bedrooms}</Avatar>}
              label={property.frontmatter.bedrooms > 1 ? "Bedrooms" : "Bedroom"}
            />
          )}
          {property.frontmatter.bathrooms > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.bathrooms}</Avatar>}
              label={
                property.frontmatter.bathrooms > 1 ? "Bathrooms" : "Bathroom"
              }
            />
          )}
          {property.frontmatter.gardens > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.gardens}</Avatar>}
              label={property.frontmatter.gardens > 1 ? "Gardens" : "Garden"}
            />
          )}
          {property.frontmatter.garages > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.garages}</Avatar>}
              label={property.frontmatter.garages > 1 ? "Garages" : "Garage"}
            />
          )}
          {property.frontmatter.parkingSpaces > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.parkingSpaces}</Avatar>}
              label={
                property.frontmatter.parkingSpaces > 1
                  ? "Parking Spaces"
                  : "Parking Space"
              }
            />
          )}
          {property.frontmatter.receptions > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.receptions}</Avatar>}
              label={
                property.frontmatter.receptions > 1 ? "Receptions" : "Reception"
              }
            />
          )}
          {/* {property.frontmatter.otherrooms > 0 && (
            <Chip
              className={classes.outline}
              avatar={<Avatar className={classes.outline}>{property.frontmatter.otherrooms}</Avatar>}
              label={
                property.frontmatter.otherrooms > 1
                  ? "Other Rooms"
                  : "Other Room"
              }
            />
          )} */}
        </div>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          color="inherit"
          variant="contained"
          disableElevation
          onClick={(): void =>
            navigate(`/properties/${property.frontmatter.id}`, {
              state: { filterOptions }
            })
          }
        >
          Learn More
        </Button>
      </CardActions>
      <Collapse timeout="auto" unmountOnExit>
        <CardContent>
          <Typography paragraph>{property.frontmatter.address}</Typography>
        </CardContent>
      </Collapse>
    </Card>
  );
};

Property.propTypes = { property: PropTypes.object };

export default Property;
